import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import type { LabelmapBinding } from '@/src/segmentation/model';
import { maskScalars } from '@/src/segmentation/model';
import type { Extent3D } from '@/src/segmentation/geometry';

export type SurfaceJob = {
  dimensions: number[]; spacing: number[]; origin: number[]; direction: number[];
  extent: Extent3D; values: Uint8Array;
};
export type SurfaceResult = { points: Float32Array; polys: Uint32Array; normals?: Float32Array; error?: string };

/** One cancellable CPU worker. It receives a cropped mask, never the CT array. */
export function createSurfaceBuilder() {
  let worker: Worker | undefined; let rejectPending: ((reason: Error) => void) | undefined;
  function cancel() {
    worker?.terminate(); worker = undefined;
    rejectPending?.(new DOMException('Surface build superseded', 'AbortError')); rejectPending = undefined;
  }
  async function build(parent: vtkImageData, binding: LabelmapBinding) {
    if (rejectPending) throw new Error('A surface job is already running');
    worker ??= new Worker(new URL('./segmentationSurface.worker.ts', import.meta.url), { type: 'module' });
    const values = maskScalars(binding.image).slice();
    const job: SurfaceJob = { dimensions: [...parent.getDimensions()], spacing: [...parent.getSpacing()],
      origin: [...parent.getOrigin()], direction: [...parent.getDirection()], extent: [...binding.extent], values };
    const result = await new Promise<SurfaceResult>((resolve,reject) => {
      rejectPending = reject;
      worker!.onmessage = event => { rejectPending = undefined; resolve(event.data); };
      worker!.onerror = event => { rejectPending = undefined; reject(new Error(event.message || 'Surface worker failed')); worker?.terminate(); worker = undefined; };
      worker!.postMessage(job, [values.buffer]);
    });
    if (result.error) throw new Error(result.error);
    const poly = vtkPolyData.newInstance(); poly.getPoints().setData(result.points,3); poly.getPolys().setData(result.polys);
    if (result.normals) poly.getPointData().setNormals(vtkDataArray.newInstance({ name:'Normals', numberOfComponents:3, values:result.normals }));
    return poly;
  }
  return { build, cancel };
}
